import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useStaffTaxCases, StaffFilter } from '../hooks/useStaffTaxCases';
import { TaxStatus, UserRole } from '../types';

interface StaffClientsPageProps {
  onCaseSelect: (caseId: string) => void;
  locationId?: string | null;
}

const StaffClientsPage: React.FC<StaffClientsPageProps> = ({ onCaseSelect, locationId }) => {
  const { user, userDoc, firmSettings } = useAuth();
  const staffRole = userDoc?.role as UserRole;
  const brandColor = firmSettings.brandColor || '#a40000';
  
  const [activeTab, setActiveTab] = useState<StaffFilter>('my_open');
  const [searchTerm, setSearchTerm] = useState('');
  
  const { cases, loading, error } = useStaffTaxCases(user?.uid, staffRole, activeTab, locationId);

  const isPrivileged = staffRole === UserRole.FirmOwner || staffRole === UserRole.Manager;

  const getStatusBadge = (status: TaxStatus) => {
    let colorClass = 'bg-slate-100 text-slate-600 border-slate-200';
    if (status === TaxStatus.INFO_RECEIVED) colorClass = 'bg-blue-50 text-blue-700 border-blue-100';
    if (status === TaxStatus.IN_PROGRESS) colorClass = 'bg-purple-50 text-purple-700 border-purple-100';
    if (status === TaxStatus.IN_REVIEW) colorClass = 'bg-amber-50 text-amber-700 border-amber-100';
    if (status === TaxStatus.READY_TO_SIGN) colorClass = 'bg-rose-50 text-rose-700 border-rose-100';
    if (status === TaxStatus.FILED) colorClass = 'bg-indigo-50 text-indigo-700 border-indigo-100';
    if (status === TaxStatus.ACCEPTED) colorClass = 'bg-emerald-50 text-emerald-700 border-emerald-100';

    return (
      <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest border ${colorClass}`}>
        {status}
      </span>
    );
  };

  const filteredCases = cases.filter(c => 
    c.clientName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const TabButton = ({ id, label }: { id: StaffFilter, label: string }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`px-4 py-2 text-xs font-bold uppercase tracking-widest rounded-lg transition-all whitespace-nowrap ${
        activeTab === id 
        ? 'bg-white text-slate-800 shadow-sm border border-slate-200' 
        : 'text-slate-400 hover:text-slate-600 hover:bg-white/50'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="max-w-full mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Client Portfolio</h1>
          <p className="text-sm text-slate-500 font-medium">
             {/* Fix: Property 'Preparer' does not exist on type 'typeof UserRole'. Using UserRole.TaxPro. */}
             {staffRole === UserRole.TaxPro 
               ? "Managing clients strictly assigned to your personal roster." 
               : "Comprehensive view of firm-wide client engagements."}
          </p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex space-x-1 bg-slate-100 p-1 rounded-xl w-fit overflow-x-auto border border-slate-200">
            {/* Fix: Property 'Preparer' does not exist on type 'typeof UserRole'. Using UserRole.TaxPro. */}
            {staffRole === UserRole.TaxPro ? (
                <>
                    <TabButton id="my_open" label="My Active" />
                    <TabButton id="my_completed" label="My Completed" />
                </>
            ) : (
                <>
                    <TabButton id="my_open" label="My Assignments" />
                    <TabButton id="firm_open" label="All Active Firm" />
                    <TabButton id="firm_completed" label="All Completed Firm" />
                </>
            )}
        </div>

        <div className="relative w-full lg:w-72">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-slate-400">
               🔍
            </div>
            <input
                type="text"
                placeholder="Search by client name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl leading-5 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm font-medium shadow-sm"
            />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24">
          <div className="w-10 h-10 border-4 border-slate-200 rounded-full animate-spin" style={{ borderTopColor: brandColor }}></div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50/50 border-b border-slate-100">
                <tr>
                  <th className="px-6 py-4 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">Client Engagement</th>
                  <th className="px-6 py-4 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">Tax Year</th>
                  {isPrivileged && (
                    <th className="px-6 py-4 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">Assigned To</th>
                  )}
                  <th className="px-6 py-4 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">Current Status</th>
                  <th className="px-6 py-4 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">Last Activity</th>
                  <th className="px-6 py-4 text-[11px] font-semibold text-slate-400 uppercase tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredCases.length === 0 ? (
                  <tr>
                    <td colSpan={isPrivileged ? 6 : 5} className="px-6 py-16 text-center">
                      <div className="text-4xl mb-3 opacity-20">👥</div>
                      <p className="text-slate-400 font-medium italic">No matching clients found in this filter.</p>
                    </td>
                  </tr>
                ) : (
                  filteredCases.map((taxCase) => (
                    <tr 
                      key={taxCase.id} 
                      className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                      onClick={() => onCaseSelect(taxCase.id)}
                    >
                      <td className="px-6 py-5">
                        <div className="font-bold text-slate-800 text-[15px]">{taxCase.clientName}</div>
                        <div className="text-[11px] text-slate-500 font-medium mt-0.5 uppercase tracking-widest">{taxCase.filingType}</div>
                      </td>
                      <td className="px-6 py-5">
                        <span className="px-2 py-1 rounded-md bg-slate-100 text-slate-600 text-[11px] font-bold">{taxCase.year}</span>
                      </td>
                      {isPrivileged && (
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-2">
                             <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[10px] text-slate-500 font-bold border border-slate-200">
                                {taxCase.assignedStaffName?.charAt(0) || '?'}
                             </div>
                             <span className="text-[13px] font-medium text-slate-700">{taxCase.assignedStaffName || 'Unassigned'}</span>
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-5">
                        {getStatusBadge(taxCase.status)}
                      </td>
                      <td className="px-6 py-5 text-sm font-medium text-slate-500 tabular-nums">
                        {taxCase.lastUpdated?.toDate?.().toLocaleDateString() || 'N/A'}
                      </td>
                      <td className="px-6 py-5 text-right">
                        <button 
                          className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-[10px] font-bold uppercase tracking-widest text-slate-600 group-hover:text-white transition-all shadow-sm"
                          style={{ ':hover': { backgroundColor: brandColor, borderColor: brandColor } } as any}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = brandColor;
                            e.currentTarget.style.borderColor = brandColor;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                            e.currentTarget.style.borderColor = '#e2e8f0';
                          }}
                        >
                          Manage File
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffClientsPage;
